export type LiveRecovery = { snapshot: Uint8Array; updates: Uint8Array[]; vector?: string }
type Pending = { value: unknown; timer: number; resolve: ((ok: boolean) => void)[] }
let database: Promise<IDBDatabase> | null = null
const pending = new Map<string, Pending>(), writes = new Map<string, Promise<boolean>>()

function openDatabase() {
  if (!database) database = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('bramble-recovery', 1)
    request.onupgradeneeded = () => request.result.createObjectStore('projects')
    request.onerror = () => reject(request.error)
    request.onblocked = () => reject(new Error('Browser recovery is blocked by another tab.'))
    request.onsuccess = () => {
      const db = request.result
      db.onversionchange = () => { db.close(); database = null }
      resolve(db)
    }
  }).catch(error => { database = null; throw error })
  return database
}

export async function readRecovery(key: string): Promise<unknown> {
  try {
    await flushRecovery(key)
    const db = await openDatabase()
    return await new Promise((resolve, reject) => {
      const request = db.transaction('projects').objectStore('projects').get(key)
      request.onsuccess = () => resolve(request.result ?? null)
      request.onerror = () => reject(request.error)
    })
  } catch { return null }
}

async function put(key: string, value: unknown) {
  try {
    const db = await openDatabase()
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction('projects', 'readwrite')
      transaction.objectStore('projects').put(value, key)
      transaction.oncomplete = () => resolve()
      transaction.onabort = transaction.onerror = () => reject(transaction.error)
    })
    return true
  } catch { return false }
}
function start(key: string) {
  const entry = pending.get(key)
  if (!entry) return writes.get(key) || Promise.resolve(true)
  pending.delete(key); clearTimeout(entry.timer)
  const write = (writes.get(key) || Promise.resolve(true)).then(() => put(key, entry.value))
  writes.set(key, write)
  void write.then(ok => {
    if (writes.get(key) === write) writes.delete(key)
    entry.resolve.forEach(resolve => resolve(ok))
  })
  return write
}
export function writeRecovery(key: string, value: unknown): Promise<boolean> {
  return new Promise(resolve => {
    const entry = pending.get(key)
    if (entry) { entry.value = value; entry.resolve.push(resolve); return }
    pending.set(key, { value, resolve: [resolve], timer: window.setTimeout(() => { void start(key) }, 40) })
  })
}
export async function flushRecovery(key: string) {
  return start(key)
}
export async function removeRecovery(key: string) {
  await flushRecovery(key)
  try {
    const db = await openDatabase()
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction('projects', 'readwrite')
      transaction.objectStore('projects').delete(key)
      transaction.oncomplete = () => resolve()
      transaction.onabort = transaction.onerror = () => reject(transaction.error)
    })
  } catch { /* A confirmed server copy remains available. */ }
}
